import { isEmpty, isNil, size } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { useReduxHook } from "../../hooks/useReduxHook";
import { useReduxLoanDrilldownHook } from "../../hooks/useReduxLoanDrilldownHook";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";

import {
  FilterAltOutlined,
  HourglassEmptyOutlined,
  SettingsOutlined,
  StarOutlineRounded,
  StarRounded,
  SubdirectoryArrowRight,
  SwapVertOutlined,
  TuneOutlined,
  UpdateRounded,
  ViewComfyOutlined,
} from "@mui/icons-material";
import { Box, IconButton, useMediaQuery } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { isBefore } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import CellRendererEditableLoanField from "../../_src_shared/agGrid/cellRenderer/CellRendererEditableLoanField";
import { columnTypesDate } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesNumber } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesNumber";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import CellRendererLoanComments from "../../components/agGrid/cellRenderer/CellRendererLoanComments";
import CellRendererLoanName from "../../components/agGrid/cellRenderer/CellRendererLoanName";
import CellRendererStackedChip from "../../components/agGrid/cellRenderer/CellRendererStackedChip";
import CellRendererUserByEmail from "../../components/agGrid/cellRenderer/CellRendererUserByEmail";

import SearchInput from "../../components/inputs/SearchInput";
import Flx from "../../components/layout/Flx";
import ScreenContent from "../../components/layout/ScreenContent";
import FilterTabSwitcher from "../../components/navigation/FilterTabSwitcher";
import Txt from "../../components/typography/Txt";
import OpenInNewTabButton from "../../components/ui/buttons/OpenInNewTabButton";

import CreateLoanButton from "../LoanPipelineScreen/buttons/CreateLoanButton";
import ButtonMenu from "../../components/ButtonMenu";
import { useUserPreferences } from "../../hooks/utils/useUserPreferences";
import { useBorrowerSubmissionsHook } from "../../hooks/useBorrowerSubmissionsHook";
import { cachedDataItemSet } from "../../redux/actions/cachedDataActions";

const PIPELINE_NAME_TO_TITLE_MAP = {
  all: "Full Pipeline",
  scenarios: "Scenario Pipeline",
  "borrower-portal-scenarios": "Borrower Portal Scenarios Pipeline",
};

const VIEW_OPTIONS = [
  { label: "All Loans", value: "all" },
  { label: "Only My Loans", value: "assigned" },
  { label: "Loan Favorites", value: "favorites" },
];

const SORT_OPTIONS = [
  {
    label: "Created Date (Newest)",
    field: "createDateTime",
    sort: "desc",
  },
  {
    label: "Created Date (Oldest)",
    field: "createDateTime",
    sort: "asc",
  },
  {
    label: "Recently Updated (Newest)",
    field: "updateDateTime",
    sort: "desc",
  },
  {
    label: "Recently Updated (Oldest)",
    field: "updateDateTime",
    sort: "asc",
  },
  {
    label: "Recently Added Comments (Newest)",
    field: "comments",
    sort: "desc",
  },
  {
    label: "Recently Added Comments (Oldest)",
    field: "comments",
    sort: "asc",
  },
];

const SORT_MENU_OPTIONS = SORT_OPTIONS.map((opt) => ({
  label: opt.label,
  value: `${opt.field}-${opt.sort}`,
}));
// ─── Preferences Persistence ─────────────────────────────────────────────────

const ASSIGNED_TO_ME_STORAGE_KEY = "loan-apps-pipeline-assigned-to-me";

const getPersistedAssignedToMe = () => {
  try {
    return localStorage.getItem(ASSIGNED_TO_ME_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const persistAssignedToMe = (value) => {
  localStorage.setItem(ASSIGNED_TO_ME_STORAGE_KEY, value ? "true" : "false");
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SUB_FILTER_NONE = "__none__";

// ─── useFavorites Hook ───────────────────────────────────────────────────────

// ─── Favorite Cell Renderer ──────────────────────────────────────────────────

// ─── ############## ──────────────────────────────────────────────────────────
// ─── Main Component ──────────────────────────────────────────────────────────
// ─── ############## ──────────────────────────────────────────────────────────
const CACHED_DATA_KEY_LOAN_APP = "loanApplication";
const CACHED_DATA_KEY_QUICK_APP = "quickApp";
const CACHED_DATA_KEY_CREDIT_AUTH = "creditAuth";
const LoanAppsScreen = ({ children }) => {
  const { syncPreferences } = useUserPreferences();

  const isFetchingRef = useRef(false);
  const gridRef = useRef(null);
  const gridReadyRef = useRef(false);

  const {
    getQuickApplications,
    getApplications,
    getCreditAuthorizations,
    loading,
  } = useBorrowerSubmissionsHook();
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  const { userEmail } = useReduxHook();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cachedData = useSelector((state) => state?.cachedData);
  const loanApps = useSelector(
    (state) => state?.cachedData?.[CACHED_DATA_KEY_LOAN_APP],
  );
  const quickApps = useSelector(
    (state) => state?.cachedData?.[CACHED_DATA_KEY_QUICK_APP],
  );
  // const { name: pipelineName } = useParams();

  const [quickFilter, setQuickFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [subStatusFilter, setSubStatusFilter] = useState(null);
  const [activeSort, setActiveSort] = useState(null);

  const [activeView, setActiveView] = useState(() => {
    if (getPersistedAssignedToMe()) return "assigned";

    return "all";
  });

  // ── assignedToMe ──
  const [assignedToMe, setAssignedToMe] = useState(() =>
    getPersistedAssignedToMe(),
  );

  const handleViewChange = useCallback(
    (val) => {
      const next = val || "all";
      setActiveView(next);
      const isAssigned = next === "assigned";

      setAssignedToMe(isAssigned);
      persistAssignedToMe(isAssigned);

      syncPreferences();
    },
    [syncPreferences],
  );

  const handleGridReady = useCallback(() => {
    gridReadyRef.current = true;
  }, []);

  useEffect(() => {
    setSubStatusFilter(null);
  }, [statusFilter]);

  // ── Sort handler ──
  const activeSortValue = useMemo(
    () => (activeSort ? `${activeSort.field}-${activeSort.sort}` : null),
    [activeSort],
  );

  const handleSortChange = useCallback(
    (val) => {
      const api = gridRef.current?.api;
      if (!api) return;
      if (!val) {
        api.applyColumnState({ defaultState: { sort: null } });
        setActiveSort(null);
        return;
      }
      const sortOption = SORT_OPTIONS.find(
        (o) => `${o.field}-${o.sort}` === val,
      );
      if (!sortOption) return;
      api.applyColumnState({
        state: [{ colId: sortOption.field, sort: sortOption.sort }],
        defaultState: { sort: null },
      });
      setActiveSort(sortOption);
    },
    [gridRef],
  );

  // ── Settings options (toggle type) ──

  const [pipeline, setPipeline] = useState(CACHED_DATA_KEY_LOAN_APP);

  const onCellClicked = useCallback((params) => {
    if (
      params?.colDef?.field === "loanNumber" ||
      params?.colDef?.field === "favorite"
    ) {
      return;
    }
    const clickedElement = params.event?.target;
    if (
      (clickedElement instanceof Element && clickedElement.closest("button")) ||
      (clickedElement instanceof Element && clickedElement.closest("a"))
    ) {
      return;
    }
    if (isNil(params?.data?.loanNumber)) {
      console.warn("No loanNumber found on this row");
      return;
    }
    if (params?.colDef?.field === "loanName") {
      // navigate(`/loan/${params.data.loanNumber}`);
    }
  }, []);

  const rowData = useMemo(() => {
    return cachedData[pipeline] || [];
  }, [pipeline, cachedData]);
  useEffect(() => {
    if (pipeline === CACHED_DATA_KEY_LOAN_APP && isNil(loanApps)) {
      getApplications({
        onSuccessFn: (data) => {
          // dispatch(loanPipelineSet(data));

          dispatch(cachedDataItemSet(CACHED_DATA_KEY_LOAN_APP, data));
          isFetchingRef.current = false;
        },
        onErrorFn: (err) => {
          console.error("GetUserPipeline error:", err);
          isFetchingRef.current = false;
        },
      });
    }
  }, [loanApps, pipeline]);

  useEffect(() => {
    if (pipeline === CACHED_DATA_KEY_QUICK_APP && isNil(quickApps)) {
      getQuickApplications({
        onSuccessFn: (data) => {
          // dispatch(loanPipelineSet(data));

          dispatch(cachedDataItemSet(CACHED_DATA_KEY_QUICK_APP, data));
          isFetchingRef.current = false;
        },
        onErrorFn: (err) => {
          console.error("GetUserPipeline error:", err);
          isFetchingRef.current = false;
        },
      });
    }
  }, [quickApps]);

  return (
    <Box sx={{ background: "#ffffff" }}>
      <Box
        sx={{
          background: "#ffffff",
          display: "flex",
          flexDirection: "column",
          p: 1,
          gap: 2,
          flexGrow: 0,
          borderBottom: "1px solid #dde1e7",
        }}
      >
        <Flx fw jb ac sx={{ borderBottom: "2px solid #dde1e7", pb: 1 }}>
          <PipelineSwitcher pipeline={pipeline} setPipeline={setPipeline} />
          <CreateLoanButton size="large" />
        </Flx>
        {/* <Flx column g={0.5} sx={{ pb: 1, flexGrow: 0 }}>
          <Flx ac>
            <PipelineStatusFilterSwitcher
              loans={filteredPipelineLoansByFavorites}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              getStatusGroupCount={getStatusGroupCount}
            />
          </Flx>
          <SubStatusSwitcher
            loans={filteredPipelineLoansByAssignment}
            statusFilter={statusFilter}
            subStatusFilter={subStatusFilter}
            setSubStatusFilter={setSubStatusFilter}
            sx={{ ml: 1 }}
          />
        </Flx> */}
      </Box>

      <ScreenContent
        sx={{
          p: 0,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ px: 2, py: 1.5, background: "#ffffff" }}>
          <Flx fw ac jb wrap g={2}>
            <Flx ac g={2} wrap>
              <SearchInput
                sx={{
                  width: "225px",
                  ".MuiInputBase-root": {
                    borderRadius: "50px !important",
                    background: "#ffffff",
                    fontSize: "11px",
                    pl: 1.2,
                    pr: 1,
                    ".MuiSvgIcon-root": {
                      fontSize: "11px",
                    },
                  },
                  input: {
                    height: "unset",
                    fontSize: "11px",
                  },
                  fieldset: {
                    border: "none",
                  },
                }}
                value={quickFilter}
                onChange={setQuickFilter}
              />

              <SortLabel label={"View"} icon={<TuneOutlined />}>
                <ButtonMenu
                  menuTitle="Table View"
                  // options={viewMenuOptions}
                  value={activeView}
                  onChange={handleViewChange}
                  buttonText={activeView === "all" ? "All Loans" : null}
                  menuMinWidth={200}
                />
              </SortLabel>

              <SortLabel label={"Sort"} icon={<SwapVertOutlined />}>
                <ButtonMenu
                  menuTitle="Sort Options"
                  options={SORT_MENU_OPTIONS}
                  value={activeSortValue}
                  onChange={handleSortChange}
                  buttonText={activeSortValue ? null : "Default"}
                  allowDeselect
                  menuMinWidth={280}
                />
              </SortLabel>
            </Flx>
          </Flx>
        </Box>

        <LoanOfficerPipelineTable
          gridRef={gridRef}
          loading={loading}
          onCellClicked={onCellClicked}
          quickFilter={quickFilter}
          rowData={rowData}
          onGridReady={handleGridReady}
        />
      </ScreenContent>
    </Box>
  );
};

// ─── Pipeline Switcher ───────────────────────────────────────────────────────

const PipelineSwitcher = ({ pipeline, setPipeline }) => {
  const cachedData = useSelector((state) => state?.cachedData);
  // const navigate = useNavigate();
  const onChange = (v) => {
    setPipeline(v);
  };

  return (
    <Switcher
      // fontSize={16}
      activeColor="#2962ff"
      fontWeight={400}
      activeFontWeight={700}
      color="#181d1f"
      value={pipeline}
      onChange={onChange}
      tabs={[
        // {
        //   label: PIPELINE_NAMES.ALL,
        //   value: "All",
        //   count: size(cachedData?.[CACHED_DATA_KEY_LOAN_APP]),
        // },
        {
          label: "Loan Applications",
          value: CACHED_DATA_KEY_LOAN_APP,
          count: size(cachedData?.[CACHED_DATA_KEY_LOAN_APP]),
        },
        {
          label: "Quick Applications",
          value: CACHED_DATA_KEY_QUICK_APP,
          count: size(cachedData?.[CACHED_DATA_KEY_QUICK_APP]),
        },
        {
          label: "Credit Authorizations",
          value: CACHED_DATA_KEY_CREDIT_AUTH,
          count: size(cachedData?.[CACHED_DATA_KEY_CREDIT_AUTH]),
        },
      ]}
    />
  );
};

const STATUS_TYPES = {
  "Not Submitted": "Not Submitted",
  Submitted: "Submitted",
};
const PipelineStatusSwitcher = ({ pipeline, setPipeline }) => {
  const cachedData = useSelector((state) => state?.cachedData);
  // const navigate = useNavigate();
  const onChange = (v) => {
    setPipeline(v);
  };

  return (
    <Switcher
      // fontSize={16}
      activeColor="#2962ff"
      fontWeight={400}
      activeFontWeight={700}
      color="#181d1f"
      value={pipeline}
      onChange={onChange}
      tabs={[
        // {
        //   label: PIPELINE_NAMES.ALL,
        //   value: "All",
        //   count: size(cachedData?.[CACHED_DATA_KEY_LOAN_APP]),
        // },
        {
          label: "Loan Applications",
          value: CACHED_DATA_KEY_LOAN_APP,
          count: size(cachedData?.[CACHED_DATA_KEY_LOAN_APP]),
        },
        {
          label: "Quick Applications",
          value: CACHED_DATA_KEY_QUICK_APP,
          count: size(cachedData?.[CACHED_DATA_KEY_QUICK_APP]),
        },
        {
          label: "Credit Authorizations",
          value: CACHED_DATA_KEY_CREDIT_AUTH,
          count: size(cachedData?.[CACHED_DATA_KEY_CREDIT_AUTH]),
        },
      ]}
    />
  );
};

// ─── AG Grid Table ───────────────────────────────────────────────────────────

const LoanOfficerPipelineTable = ({
  gridRef,
  rowData,
  onRowClicked,
  quickFilter,
  loading,
  onCellClicked,
  onGridReady,
  showFavorites,
  toggleFavorite,
  isFavorite,
}) => {
  const columnDefs = useMemo(
    () => [
      { hide: true, headerName: "_id", field: "_id" },
      {
        headerName: "Loan #",
        field: "loanNumber",
        minWidth: 90,
        maxWidth: 90,
        width: 90,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        flex: 0,
        getQuickFilterText: (params) =>
          params.value ? String(params.value) : "",
        filter: "agTextColumnFilter",
        cellRenderer: (params) => (
          <OpenInNewTabButton linkIcon to={`/loan/${params?.data?.loanNumber}`}>
            {params?.data?.loanNumber}
          </OpenInNewTabButton>
        ),
        suppressFloatingFilterButton: true,
      },
      {
        field: "sync_loan_id",
        headerName: "External Loan ID",
        hide: true,
        width: 150,
        minWidth: 150,
        flex: 0,
      },
      {
        headerName: "Created",
        field: "createDateTime",
        type: "date",
        minWidth: 115,
        maxWidth: 115,
        width: 115,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        wrapHeaderText: true,
        cellRenderer: (p) => (
          <CellRendererStackedChip
            icon={<HourglassEmptyOutlined className="thin-8" />}
            params={p}
          />
        ),
      },
      {
        headerName: "Updated",
        field: "updateDateTime",
        type: "date",
        minWidth: 115,
        maxWidth: 115,
        width: 115,
        wrapHeaderText: true,
        suppressHeaderFilterButton: true,
        cellRenderer: (p) => (
          <CellRendererStackedChip
            icon={<UpdateRounded className="thin-8" />}
            params={p}
          />
        ),
      },
      {
        headerName: "Loan Name",
        field: "loanName",
        minWidth: 280,
        width: 360,
        cellRenderer: CellRendererLoanName,
        suppressSizeToFit: true,
        valueGetter: (params) =>
          isNil(params?.data?.loanName)
            ? `Loan #${params?.data?.loanNumber}`
            : params?.data?.loanName,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        autoHeight: true,
        suppressFloatingFilterButton: true,
        headerTooltip: "Go to loan",
        cellStyle: { cursor: "pointer" },
      },
      {
        headerName: "Comments",
        field: "comments",
        suppressAutoSize: true,
        suppressSizeToFit: true,
        suppressHeaderFilterButton: true,
        width: 600,
        minWidth: 350,
        flex: 0,
        autoHeight: true,
        wrapText: true,
        suppressFloatingFilterButton: true,
        cellStyle: {
          paddingTop: "14px",
          paddingBottom: "14px",
          paddingLeft: 0,
          cursor: "pointer",
        },
        cellRenderer: CellRendererLoanComments,
        cellClass: (params) => {
          if (isEmpty(params?.value)) return "ag-cell-vertical-center";
        },
        comparator: (valueA, valueB) => {
          const hasA = valueA && Array.isArray(valueA) && valueA.length > 0;
          const hasB = valueB && Array.isArray(valueB) && valueB.length > 0;
          if (!hasA && !hasB) return 0;
          if (!hasA) return -1;
          if (!hasB) return 1;
          return (
            new Date(valueA[0].commentDateTime).getTime() -
            new Date(valueB[0].commentDateTime).getTime()
          );
        },
      },
      {
        headerName: "Expected Closing Date",
        field: "expectedClosingDate",
        type: "date",
        flex: 0,
        width: 180,
        cellRenderer: CellRendererEditableLoanField,
        cellStyle: (p) => {
          const valueDate = p?.value ? new Date(p.value) : null;
          const isPastDate = valueDate && isBefore(valueDate, new Date());
          const closedStatuses = [
            "Closed",
            "Closed w/ Post Closing Conditions",
            "Balance Sheet",
            "Loan Sold",
            "Inactive",
            "Dead",
          ];
          const isClosedLoan = closedStatuses.includes(p?.data?.loanStatus);
          if (isPastDate) {
            return {
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              background: isClosedLoan ? "#e8f5e9ab" : "#ffebee6b",
            };
          }
          return { cursor: "pointer", display: "flex", alignItems: "center" };
        },
      },
      {
        headerName: "Loan Product Type",
        field: "loanProductType",
        flex: 0,
        width: 200,
      },
      {
        headerName: "Loan Purpose",
        field: "loanPurpose",
        flex: 0,
        width: 200,
      },
      {
        headerName: "Loan Status",
        field: "loanStatus",
        minWidth: 180,
        cellStyle: {
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        },
        flex: 0,
      },
      {
        headerName: "Sub Status",
        field: "subStatus",
        flex: 0,
        width: 200,
        minWidth: 150,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Salesperson",
        field: "salesperson",
        cellRenderer: (p) => <CellRendererUserByEmail {...p} stacked={true} />,
      },

      // ----------- DEFAULT HIDDEN -----------
      { headerName: "Asset ID", field: "assetId", hide: true },
      { field: "baseLoanToAsIsValue", type: "percent", hide: true },
      { field: "baseLoanToPurchasePrice", type: "percent", hide: true },
      {
        headerName: "Base Loan Amount",
        field: "baseLoanAmount",
        type: "dollar",
        cellRenderer: CellRendererEditableLoanField,
        hide: true,
      },
      {
        headerName: "Borrowers",
        field: "borrowers",
        valueGetter: (params) => {
          const v = params.data?.borrowers;
          if (isEmpty(v) || isNil(v)) return;
          return v.map((item) =>
            isEmpty(item) || isNil(item?.firstName) || isNil(item?.lastName)
              ? "N/A"
              : `${item.firstName} ${item.lastName}`,
          );
        },
        hide: true,
      },
      {
        headerName: "Closing Scheduled",
        field: "closingScheduled",
        type: "boolean",
        hide: true,
      },
      {
        headerName: "Closing Scheduled Date",
        field: "closingScheduledDate",
        type: "boolean",
        hide: true,
      },
      { field: "closingMonth", hide: true },
      { field: "closingQuarter", hide: true },
      { field: "closingWeek", hide: true },
      {
        headerName: "Credit Authorizations",
        field: "creditAuthorizations",
        hide: true,
      },
      {
        headerName: "Discount Points",
        field: "discountPoints",
        type: "percent",
        hide: true,
      },
      { headerName: "Dutch Interest", field: "dutchInterest", hide: true },
      {
        headerName: "Existing Loan In Default",
        field: "existingLoanInDefault",
        type: "boolean",
        hide: true,
      },
      {
        headerName: "Expected Closing Date",
        field: "expectedClosingDate",
        type: "date",
        minWidth: 180,
        cellRenderer: CellRendererEditableLoanField,
        cellStyle: (p) => {
          const valueDate = p?.value ? new Date(p.value) : null;
          const isPastDate = valueDate && isBefore(valueDate, new Date());
          const closedStatuses = [
            "Closed",
            "Closed w/ Post Closing Conditions",
            "Balance Sheet",
            "Loan Sold",
            "Inactive",
            "Dead",
          ];
          const isClosedLoan = closedStatuses.includes(p?.data?.loanStatus);
          if (isPastDate) {
            return {
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              background: isClosedLoan ? "#e8f5e9ab" : "#ffebee6b",
            };
          }
          return { cursor: "pointer", display: "flex", alignItems: "center" };
        },
        hide: true,
      },
      {
        headerName: "Funded Date",
        field: "fundedDate",
        type: "date",
        hide: true,
      },
      {
        headerName: "Interest Rate",
        field: "interestRate",
        type: "percent",
        hide: true,
      },
      {
        headerName: "Is Balance Sheet Closing",
        field: "isBalanceSheetClosing",
        hide: true,
      },
      {
        headerName: "Is Table Funding",
        field: "isTableFunding",
        type: "boolean",
        hide: true,
      },
      {
        headerName: "Last Viewed",
        field: "lastViewDateTime",
        type: "date",
        minWidth: 200,
        hide: true,
      },
      { headerName: "Loan Closer", field: "loanCloser", hide: true },
      { headerName: "Loan Documents", field: "loanDocuments", hide: true },
      { headerName: "Loan Product Type", field: "loanProductType", hide: true },
      { headerName: "Loan Purpose", field: "loanPurpose", flex: 0, hide: true },
      {
        headerName: "Maturity Date",
        field: "maturityDate",
        type: "date",
        hide: true,
      },
      {
        headerName: "Milestone Status Changes",
        field: "milestoneStatusChanges",
        hide: true,
      },
      {
        headerName: "Net Funding",
        field: "netFunding",
        type: "dollar",
        hide: true,
      },
      {
        headerName: "Origination Date",
        field: "originationDate",
        type: "date",
        hide: true,
      },
      {
        headerName: "Origination Points",
        field: "originationPoints",
        type: "percent",
        hide: true,
      },
      { headerName: "Permitted Users", field: "permittedUsers", hide: true },
      {
        headerName: "Pipeline",
        field: "pipeline",
        width: 140,
        flex: 0,
        hide: true,
      },
      { headerName: "Processor", field: "processor", hide: true },
      {
        headerName: "Referral Source",
        field: "referralSource",
        cellRenderer: CellRendererEditableLoanField,
        valueFormatter: (params) => params?.data?.referralSource || "None",
        hide: true,
      },
      {
        headerName: "Subject Properties",
        field: "subjectProperties",
        valueGetter: (params) => {
          const v = params.data?.subjectProperties;
          if (isEmpty(v) || isNil(v)) return;
          return v.map((item) =>
            isEmpty(item) || isNil(item?.address?.fullAddress)
              ? "N/A"
              : `${item.address.fullAddress}`,
          );
        },
        hide: true,
      },
      { field: "totalArv", type: "dollar", hide: true },
      { field: "totalAsIsValue", type: "dollar", hide: true },
      { field: "totalConstructionBudget", type: "dollar", hide: true },
      { field: "totalCostBasis", type: "dollar", hide: true },
      {
        headerName: "Total Holdback",
        field: "totalHoldback",
        type: "dollar",
        hide: true,
      },
      {
        headerName: "Total Loan Amount",
        field: "totalLoanAmount",
        type: "dollar",
        hide: true,
      },
      { field: "totalLoanToArv", type: "percent", hide: true },
      { field: "totalPurchasePrice", type: "dollar", hide: true },
      { headerName: "Underwriter", field: "underwriter", hide: true },
    ],
    [showFavorites],
  );

  const getRowId = useCallback((params) => params.data._id, []);

  const defaultColDef = useMemo(
    () => ({
      cursor: "pointer",
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      showRowGroup: false,
      sortable: true,
      resizable: true,
      cellClass: "ag-cell-vertical-center",
      minWidth: 200,
      filterParams: { buttons: ["clear", "apply"] },
    }),
    [],
  );

  const handleOnRowClicked = useCallback(
    (params) => {
      if (params.node.group) {
        params.node.setExpanded(!params.node.expanded);
        return;
      }
      onRowClicked?.(params?.data);
    },
    [onRowClicked],
  );

  const columnTypes = useMemo(
    () => ({ ...columnTypesDate(), ...columnTypesNumber() }),
    [],
  );

  const sideBar = useMemo(() => ({ toolPanels: ["columns", "filters"] }), []);

  const gridContext = useMemo(
    () => ({ toggleFavorite, isFavorite }),
    [toggleFavorite, isFavorite],
  );

  return (
    <Box
      sx={{
        borderRadius: "0px",
        ".ag-theme-quartz .ag-root-wrapper": {
          borderLeft: "none !important",
          borderRight: "none !important",
          borderBottom: "none !important",
          borderRadius: "0px !important",
        },
      }}
    >
      <AgGridTableWrapper height="auto" compactTable>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          columnTypes={columnTypes}
          getRowId={getRowId}
          quickFilterText={quickFilter}
          defaultColDef={defaultColDef}
          sideBar={sideBar}
          onCellClicked={onCellClicked}
          loading={loading}
          onRowClicked={handleOnRowClicked}
          onGridReady={onGridReady}
          context={gridContext}
          tooltipShowDelay={100}
          pagination={true}
          domLayout="autoHeight"
          paginationPageSize={20}
          headerHeight={54}
          enableCellTextSelection
        />
      </AgGridTableWrapper>
    </Box>
  );
};

// ─── Reusable Switcher ───────────────────────────────────────────────────────

const SortLabel = ({ icon, children, label }) => {
  return (
    <Flx ac gap={0.6} sx={{ mr: 2 }}>
      <Flx
        ac
        gap={0.4}
        sx={{
          svg: {
            fontSize: "15px",
            opacity: 0.5,
          },
        }}
      >
        {icon}
        <Txt sx={{ fontSize: "12px" }} secondary>
          {label}
        </Txt>
      </Flx>
      {children}
    </Flx>
  );
};

const Switcher = ({
  tabs,
  onChange,
  value,
  sx,
  column = false,
  buttonPadding = { px: 1.75, py: 0.75 },
  fontSize = 14,
  backgroundColor,
  activeBackgroundColor,
  activeColor = "#2962ff",
  color,
  ...rest
}) => {
  return (
    <FilterTabSwitcher
      fontSize={fontSize}
      value={value}
      activeBackgroundColor={activeBackgroundColor}
      buttonPadding={buttonPadding}
      backgroundColor={backgroundColor}
      activeColor={activeColor || "#2962ff"}
      fontWeight={400}
      color={color || "#181d1fb4"}
      activeFontWeight={700}
      sx={{
        flexWrap: "wrap",
        flexGrow: 0,
        gap: 0,
        width: "max-content",
        border: "1px solid #e5e3df",
        ".MuiButtonBase-root": {},
        ...sx,
      }}
      onChange={onChange}
      tabs={tabs}
      {...rest}
    />
  );
};

export default LoanAppsScreen;
